import { useState } from 'react';
import type { MealSlot, MealTemplateInput, MealTemplateRow } from '@st/contract';
import {
  useDeleteMealTemplate,
  useSaveMealTemplate,
  useTrainerMealTemplates,
} from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { PageHeader } from '../../components/PageHeader';
import { useTranslation } from '../../i18n/I18nProvider';
import { toMutationMessage } from '../../i18n/errorMessage';
import {
  Badge,
  Button,
  ConfirmDialog,
  Input,
  Modal,
  NumberInput,
  Select,
  SkeletonList,
  Textarea,
  useToast,
} from '../../ui';

export const SLOT_KEY: Record<MealSlot, string> = {
  breakfast: 'nutrition.slotBreakfast',
  lunch: 'nutrition.slotLunch',
  dinner: 'nutrition.slotDinner',
  snack: 'nutrition.slotSnack',
};

/**
 * Trenerovi predlošci obroka.
 *
 * Nastavak prijedloga iz klijentovih ranijih obroka: ondje brojke dolaze od
 * klijenta, ovdje ih određuje Mario. Klijent koji ne zna izračunati
 * makronutrijente dobiva gotov unos, a trener kontrolu nad time što se nudi.
 *
 * Brojke su namjerno neobavezne — predložak se smije ponuditi odmah i dopuniti
 * poslije, inače bi jelovnik čekao da netko sjedne i sve izračuna.
 */
export function TrainerMealTemplates() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const query = useTrainerMealTemplates();
  const save = useSaveMealTemplate();
  const remove = useDeleteMealTemplate();

  const [editing, setEditing] = useState<MealTemplateRow | 'new' | null>(null);
  const [brisanje, setBrisanje] = useState<MealTemplateRow | null>(null);

  return (
    <>
      <PageHeader
        eyebrow={t('shell.trainer')}
        title={t('mealTemplates.title')}
        action={
          <Button size="sm" onClick={() => setEditing('new')}>
            {t('mealTemplates.new')}
          </Button>
        }
      />
      <p className="mb-4 max-w-[62ch] text-[14px] leading-relaxed text-ink-muted">
        {t('mealTemplates.intro')}
      </p>

      <QueryBoundary
        query={query}
        skeleton={<SkeletonList rows={4} />}
        isEmpty={(d) => d.items.length === 0}
        empty={{
          message: t('mealTemplates.empty'),
          actionLabel: t('mealTemplates.new'),
          onAction: () => setEditing('new'),
        }}
      >
        {(data) => (
          <ul className="grid gap-3 sm:grid-cols-2">
            {data.items.map((predlozak) => (
              <li
                key={predlozak.id}
                className="flex flex-col gap-2 rounded-xl border border-line-subtle bg-surface p-4 shadow-card"
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="min-w-0">
                    <span className="block text-[15px] text-ink">{predlozak.name}</span>
                    <span className="block text-[12px] text-ink-muted">
                      {predlozak.slot ? t(SLOT_KEY[predlozak.slot]) : t('mealTemplates.anySlot')}
                    </span>
                  </span>
                  {predlozak.kcal === null ? null : (
                    <Badge tone="accent">{`${predlozak.kcal} kcal`}</Badge>
                  )}
                </div>

                {(predlozak.proteinG ?? predlozak.carbsG ?? predlozak.fatG) === null ? null : (
                  <p className="text-[12.5px] tabular-nums text-ink-muted">
                    {[
                      [t('nutrition.protein'), predlozak.proteinG],
                      [t('nutrition.carbs'), predlozak.carbsG],
                      [t('nutrition.fat'), predlozak.fatG],
                    ]
                      .filter(([, v]) => v !== null)
                      .map(([oznaka, v]) => `${String(oznaka)} ${String(v)} g`)
                      .join(' · ')}
                  </p>
                )}

                {predlozak.note && (
                  <p className="text-[13px] leading-relaxed text-ink-muted">{predlozak.note}</p>
                )}

                <div className="mt-1 flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(predlozak)}>
                    {t('action.edit')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setBrisanje(predlozak)}>
                    {t('action.delete')}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </QueryBoundary>

      {editing && (
        <TemplateForm
          predlozak={editing === 'new' ? null : editing}
          naslov={editing === 'new' ? t('mealTemplates.newTitle') : t('mealTemplates.editTitle')}
          pending={save.isPending}
          onClose={() => setEditing(null)}
          onSubmit={(input) =>
            save.mutate(
              { ...(editing === 'new' ? {} : { id: editing.id }), input },
              {
                onSuccess: () => {
                  setEditing(null);
                  toast(t('mealTemplates.saved'), 'success');
                },
                onError: (error) => toast(toMutationMessage(t, error), 'danger'),
              },
            )
          }
        />
      )}

      {brisanje && (
        <ConfirmDialog
          open
          tone="danger"
          title={t('mealTemplates.deleteTitle')}
          // Imenuje SE što se briše — „Jesi li siguran?" ne kaže ništa.
          question={t('mealTemplates.deleteQuestion', { name: brisanje.name })}
          confirmLabel={t('action.delete')}
          cancelLabel={t('common.cancel')}
          closeLabel={t('a11y.closeDialog')}
          loading={remove.isPending}
          onClose={() => setBrisanje(null)}
          onConfirm={() =>
            remove.mutate(brisanje.id, {
              onSuccess: () => {
                setBrisanje(null);
                toast(t('mealTemplates.deleted'), 'success');
              },
              onError: (error) => toast(toMutationMessage(t, error), 'danger'),
            })
          }
        />
      )}
    </>
  );
}

/** Prazno polje je legitimno stanje: brojku koja se ne zna ne izmišljamo nulom. */
type Broj = number | '';

/** Vrijednosti kojima se obrazac otvara — postojeći predložak ili klijentov obrok. */
export type PocetniPredlozak = Omit<MealTemplateRow, 'id'>;

/**
 * Obrazac predloška.
 *
 * Izvezen jer se otvara s dva mjesta: iz popisa predložaka i iz klijentovog
 * obroka na trenerovom ekranu prehrane. Drugi put je bitniji nego što zvuči —
 * jelovnik koji treba prvo utipkati ostaje prazan, a Mario obroke svojih
 * klijenata ionako pregledava svaki dan.
 */
export function TemplateForm({
  predlozak,
  naslov,
  pending,
  onClose,
  onSubmit,
}: {
  predlozak: PocetniPredlozak | null;
  naslov: string;
  pending: boolean;
  onClose: () => void;
  onSubmit: (input: MealTemplateInput) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(predlozak?.name ?? '');
  const [slot, setSlot] = useState<string>(predlozak?.slot ?? '');
  const [kcal, setKcal] = useState<Broj>(predlozak?.kcal ?? '');
  const [proteinG, setProteinG] = useState<Broj>(predlozak?.proteinG ?? '');
  const [carbsG, setCarbsG] = useState<Broj>(predlozak?.carbsG ?? '');
  const [fatG, setFatG] = useState<Broj>(predlozak?.fatG ?? '');
  const [note, setNote] = useState(predlozak?.note ?? '');
  const [error, setError] = useState<string | undefined>(undefined);

  function submit() {
    if (!name.trim()) {
      setError(t('mealTemplates.nameRequired'));
      return;
    }
    setError(undefined);
    onSubmit({
      name: name.trim(),
      slot: slot ? (slot as MealSlot) : null,
      kcal: kcal === '' ? null : Math.round(kcal),
      proteinG: proteinG === '' ? null : proteinG,
      carbsG: carbsG === '' ? null : carbsG,
      fatG: fatG === '' ? null : fatG,
      note: note.trim() || null,
    });
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={naslov}
      closeLabel={t('a11y.closeDialog')}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" loading={pending} onClick={submit}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Input
          id="template-name"
          label={t('mealTemplates.name')}
          hint={t('mealTemplates.nameHint')}
          value={name}
          required
          error={error}
          onChange={(e) => setName(e.target.value)}
        />
        <Select
          id="template-slot"
          label={t('mealTemplates.slot')}
          hint={t('mealTemplates.slotHint')}
          value={slot}
          onChange={(e) => setSlot(e.target.value)}
          placeholder={t('mealTemplates.anySlot')}
          options={(['breakfast', 'lunch', 'dinner', 'snack'] as const).map((v) => ({
            value: v,
            label: t(SLOT_KEY[v]),
          }))}
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <NumberInput
            id="template-kcal"
            label={t('nutrition.kcal')}
            value={kcal}
            onValueChange={setKcal}
          />
          <NumberInput
            id="template-protein"
            label={t('nutrition.protein')}
            suffix="g"
            value={proteinG}
            onValueChange={setProteinG}
          />
          <NumberInput
            id="template-carbs"
            label={t('nutrition.carbs')}
            suffix="g"
            value={carbsG}
            onValueChange={setCarbsG}
          />
          <NumberInput
            id="template-fat"
            label={t('nutrition.fat')}
            suffix="g"
            value={fatG}
            onValueChange={setFatG}
          />
        </div>
        <Textarea
          id="template-note"
          label={t('mealTemplates.note')}
          hint={t('mealTemplates.noteHint')}
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </form>
    </Modal>
  );
}
